import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  placement?: 'top' | 'bottom';
  wrapperClassName?: string;
}

const VIEWPORT_PADDING = 8;

const TooltipContent: React.FC<{
  content: React.ReactNode;
  placement: 'top' | 'bottom';
  anchorX: number;
  anchorY: number;
}> = ({ content, placement, anchorX, anchorY }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [adjustedX, setAdjustedX] = useState(anchorX);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const { offsetWidth } = ref.current;
    const vw = window.innerWidth;
    const rawLeft = anchorX - offsetWidth / 2;
    const clamped = Math.min(Math.max(rawLeft, VIEWPORT_PADDING), vw - offsetWidth - VIEWPORT_PADDING);
    setAdjustedX(clamped + offsetWidth / 2);
  }, [anchorX]);

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        left: adjustedX,
        top: anchorY,
        transform: placement === 'top'
          ? 'translate(-50%, calc(-100% - 8px))'
          : 'translate(-50%, 8px)',
        zIndex: 9999,
        pointerEvents: 'none',
      }}
      className="flex flex-col items-center"
    >
      {placement === 'bottom' && (
        <span className="w-2 h-2 bg-slate-900 dark:bg-white rotate-45 shrink-0 -mb-1" />
      )}
      <span className="block bg-slate-900 dark:bg-white px-3 py-1.5 text-[10px] font-bold text-white dark:text-slate-900 uppercase tracking-widest leading-none whitespace-nowrap">
        {content}
      </span>
      {placement === 'top' && (
        <span className="w-2 h-2 bg-slate-900 dark:bg-white rotate-45 shrink-0 -mt-1" />
      )}
    </div>
  );
};

const Tooltip: React.FC<TooltipProps> = ({ content, children, placement = 'top', wrapperClassName = '' }) => {
  const [visible, setVisible] = useState(false);
  const [anchor, setAnchor] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);

  const show = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setAnchor({
      x: rect.left + rect.width / 2,
      y: placement === 'top' ? rect.top : rect.bottom,
    });
    setVisible(true);
  }, [placement]);

  const hide = useCallback(() => setVisible(false), []);

  return (
    <span
      ref={triggerRef}
      className={`relative inline-flex ${wrapperClassName}`}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}
      {visible && ReactDOM.createPortal(
        <TooltipContent
          content={content}
          placement={placement}
          anchorX={anchor.x}
          anchorY={anchor.y}
        />,
        document.body,
      )}
    </span>
  );
};

export default Tooltip;
